// =====================================================================
// The magic go-faster button — detecting, and talking an SDR through disabling,
// Chrome's post-quantum key agreement for WebRTC.
//
// Chrome offers a post-quantum key agreement (ML-KEM) for WebRTC by default. Its
// key share pushes the DTLS ClientHello past the path MTU, so the first flight
// fragments; on a lossy long-haul path a lost fragment costs a full retransmit
// timer before the SDR can hear anything. Turning it off is a browser flag we
// cannot set — no WebRTC API exposes key agreement, and a page cannot read or
// write chrome://flags. So this module exists to DETECT the condition from our own
// call telemetry and hand the SDR the four steps.
//
// Pure: no `window`, no storage, no clock. The hook owns all of that.
// =====================================================================

// Deep-links to and highlights the specific flag row. A page cannot make a
// chrome:// URL clickable, so this is always copy-and-paste — which is why the
// exact anchor matters more than usual: a typo lands them on the flags page with
// nothing highlighted, hunting through a list of hundreds.
export const FLAG_ID = 'webrtc-pqc-for-dtls';
export const FLAGS_URL = `chrome://flags/#${FLAG_ID}`;

// The heading Chrome puts on the row, so step 3 names what they are looking at.
export const FLAG_LABEL = 'WebRTC PQC for DTLS';

// Chrome removes the flag (and the equivalent enterprise policy) at this version.
// Past it the instruction is inert, so the whole feature hides itself rather than
// nagging people to set something that no longer exists.
export const FLAG_REMOVED_IN = 152;

export interface UaDataLike {
  brands?: ReadonlyArray<{ brand: string; version: string }>;
}

// Every Chromium fork carries `Chrome/` in its UA and a Chromium brand in
// userAgentData. Recognising them is how we EXCLUDE them: this ships to Chrome
// only, so a naive `Chrome/` test would hand Edge and Brave users a chrome:// URL
// that does not resolve in their browser.
const FORKS = ['Edg/', 'EdgA/', 'OPR/', 'Opera', 'Brave', 'Vivaldi', 'YaBrowser', 'SamsungBrowser'];
const FORK_BRANDS = ['edge', 'opera', 'brave', 'vivaldi', 'yandex', 'samsung'];

// chrome://flags exists on mobile but the UI and the SDR's situation are different
// enough that the four steps would be wrong. Desktop only.
const MOBILE = ['Android', 'iPhone', 'iPad', 'iPod', 'CriOS', 'Mobile'];

// The Chrome major version, or null to hide the feature entirely.
export function detectChrome(ua: string, uaData?: UaDataLike | null): number | null {
  if (typeof ua !== 'string' || ua === '') return null;
  if (MOBILE.some((m) => ua.includes(m))) return null;

  // Preferred: brands is structured and names the fork outright, where the UA
  // string only implies it.
  const brands = uaData?.brands;
  if (Array.isArray(brands) && brands.length > 0) {
    const named = brands.map((b) => (typeof b?.brand === 'string' ? b.brand.toLowerCase() : ''));
    if (named.some((b) => FORK_BRANDS.some((f) => b.includes(f)))) return null;
    const chrome = brands.find((b) => b?.brand?.toLowerCase() === 'google chrome');
    if (chrome) return majorOf(chrome.version);
    // Chromium-branded but not Google Chrome: an unbadged build we do not support.
    return null;
  }

  if (FORKS.some((f) => ua.includes(f))) return null;
  const m = /Chrome\/(\d+)/.exec(ua);
  return m ? majorOf(m[1]) : null;
}

function majorOf(raw: string | undefined): number | null {
  const n = Number.parseInt(String(raw ?? ''), 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n < FLAG_REMOVED_IN ? n : null;
}

// 'affected' = post-quantum active, calls are slow. 'fixed' = it is off. Named for
// what they MEAN rather than for the flag's position — an earlier draft used
// 'on'/'off', where 'off' meant good, which is one refactor away from silently
// prompting the fixed and confirming the broken.
export type PqVerdict = 'affected' | 'fixed' | 'unknown';

// TWO thresholds with a deliberate gap between them, not one dividing line.
//
// A single cut at 300 was shipping ✅ "engaged" to SDRs measuring 252, 255 and 296
// whose calls were no faster than the unfixed people beside them — one flipped
// affected (443) to fixed (255) inside half an hour. Telling someone their problem
// is solved when it is not is the worst thing this feature can do: they stop
// looking, and we lose the report.
//
// So the bands are drawn around the CLEAN clusters only:
//   <=200  post-quantum off  (observed 143–175 on confirmed-fixed browsers)
//   >=350  post-quantum on   (observed 373–707)
//   between: we cannot tell, and say so.
//
// The dead zone is wide on purpose. Everything ambiguous seen in production —
// 213, 232, 239, 252, 255, 264, 296, 298, 318, 347 — falls inside it. Being
// unsure costs a vaguer caption; being wrong costs the SDR's trust.
//
// Still provisional in the sense that a same-browser before/after has not run.
// Narrowing them further is safe; widening them needs that data first.
export const PQ_FIXED_MAX = 200;
export const PQ_AFFECTED_MIN = 350;

// One call's handshake bytes is a noisy sample; the median of the recent few is
// not. Kept small because an SDR who fixes it mid-shift should see that reflected
// within a few calls, not at the end of the day.
export const MAX_SAMPLES = 5;

// One call is enough to leave 'unknown' — the post-relaunch copy promises "your
// next call", and it has to mean it. Changing between two REAL verdicts still
// needs SETTLE_RUN agreeing samples; see settleVerdict.
export const MIN_SAMPLES = 1;

// Exported so the telemetry can report the number the verdict was derived from —
// a threshold that turns out to be wrong is then diagnosable from the same rows.
export function medianOf(samples: readonly number[]): number | null {
  const usable = samples.filter((n) => typeof n === 'number' && Number.isFinite(n) && n >= 0);
  if (usable.length === 0) return null;
  const sorted = [...usable].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2
    : (sorted[mid] ?? 0);
}

export function rawVerdict(samples: readonly number[]): PqVerdict {
  const usable = samples.filter((n) => typeof n === 'number' && Number.isFinite(n) && n >= 0);
  if (usable.length < MIN_SAMPLES) return 'unknown';
  const median = medianOf(usable);
  if (median == null) return 'unknown';
  if (median <= PQ_FIXED_MAX) return 'fixed';
  if (median >= PQ_AFFECTED_MIN) return 'affected';
  // The dead zone. We genuinely cannot tell, so we say nothing rather than
  // guessing — 'unknown' still shows the button, just without a claim attached.
  return 'unknown';
}

// Hysteresis state. A (previous, raw) signature cannot express "consecutive
// agreeing samples" — the streak has to live somewhere, so it lives here.
export interface VerdictState {
  verdict: PqVerdict; // what the UI shows
  candidate: PqVerdict; // what recent samples suggest
  run: number; // consecutive samples agreeing with `candidate`
}

export const SETTLE_RUN = 2;

export function newVerdictState(): VerdictState {
  return { verdict: 'unknown', candidate: 'unknown', run: 0 };
}

export function settleVerdict(prev: VerdictState, raw: PqVerdict): VerdictState {
  // Never leaves 'fixed'. A browser cannot silently re-enable the flag under a
  // running tab, so a flip back is measurement noise by definition — and an SDR
  // watching their confirmation turn back into a nag would reasonably stop
  // trusting either state.
  if (prev.verdict === 'fixed') return prev;
  if (raw === 'unknown') return prev;

  // 'unknown' is the absence of a measurement, not a competing one, so the first
  // real sample is adopted immediately rather than waiting out a streak.
  if (prev.verdict === 'unknown') return { verdict: raw, candidate: raw, run: 0 };

  if (raw === prev.verdict) return { verdict: prev.verdict, candidate: prev.verdict, run: 0 };

  const run = raw === prev.candidate ? prev.run + 1 : 1;
  if (run >= SETTLE_RUN) return { verdict: raw, candidate: raw, run: 0 };
  return { verdict: prev.verdict, candidate: raw, run };
}

// What the SDR is shown.
//
// NO SUCCESS STATE, for anyone. There was one — ✅ "Go-faster button engaged" —
// and it was wrong in production: SDRs measuring 252, 255 and 296 bytes were told
// their problem was solved while their calls ran 2,600–3,189ms, no faster than the
// unfixed people beside them. One was shown ✅ at median 255 and measured 422 an
// hour later; she had never been fixed at all.
//
// Widening the bands would have caught those three, but not the underlying problem:
// we cannot yet tell a fixed browser from a lucky network path, because the only
// two SDRs ever to measure in the fixed cluster are both on a browser whose
// post-quantum default we have not established. Until a same-browser before/after
// settles that, any success claim is a guess, and a guess that says "you're done"
// is worse than silence — the SDR stops looking and we lose the report.
//
// So the UI asserts only what it can support: either we have measured you as slow,
// or we have not. The verdict is still computed and logged so the data keeps
// accumulating toward the threshold that would let the success state come back.
//
// 'hidden' is reserved for browsers that cannot act on it at all.
export type PqUi = 'hidden' | 'offer' | 'prompt';

export function pqUi(verdict: PqVerdict): PqUi {
  return verdict === 'affected' ? 'prompt' : 'offer';
}

// A record rather than a bare flag, so the date is available later without a
// migration. Read and written only by the hook — this module stays free of
// `window`.
export const LS_PQ_APPLIED = 'maestro.pq.applied';

export function encodeApplied(nowMs: number): string {
  return JSON.stringify({ appliedAt: nowMs });
}

// Anything malformed reads as "not applied" rather than throwing, on a path that
// runs during render.
export function decodeApplied(raw: string | null): boolean {
  if (!raw) return false;
  try {
    const v = JSON.parse(raw) as { appliedAt?: unknown };
    return typeof v?.appliedAt === 'number' && Number.isFinite(v.appliedAt);
  } catch {
    return false;
  }
}
